/**
 * RoyaleStrip
 *
 * The single-row status strip above the board (replaces the old stacked
 * RoyaleClock + ScoreComboCard). Per the user's UI-2 decision, ONE row holds,
 * left → right:
 *   points (+ piece glyph) · combo pips · current leaderboard rank ·
 *   who-to-play pill · PlayChess-style clock (bar + mm:ss)
 *
 * Collapsing to one row frees vertical space so the board can be larger.
 * Presentational — the parent passes everything in.
 */

import React, {useEffect, useRef, useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import CCText from '@/Atoms/CCText';
import {FontSizes} from '@/Utils/Dimensions';

import type {PieceTier} from '../royaleTypes';

const TEAL_DARK = '#1F6C63';
const BROWN = '#4D3F37';
const CREAM3 = '#EDE4CC';
const YELLOW = '#FACF47';


const PIECE_GLYPHS: Record<PieceTier | 'king', string> = {
  pawn: '♟',
  knight: '♞',
  bishop: '♝',
  rook: '♜',
  queen: '♛',
  king: '♚',
};

interface Props {
  /**
   * Spectator mode: ONE combined row — "Watching <name>" + the followed
   * player's points/combo tile/piece + the shared race clock (the spectator
   * isn't playing, so no personal rank/to-move). When no player is followed
   * yet it shows a plain "Spectating" label.
   */
  spectator?: boolean;
  /** Followed player's display name (spectator mode). */
  followedName?: string;
  /** Whether the followed player is eliminated (spectator mode). */
  followedEliminated?: boolean;
  /** Spectator mode: stop-watching handler — rendered as an inline chip. */
  onStopWatching?: () => void;
  points?: number;
  combo?: number;
  piece?: PieceTier | 'king';
  /** 1-based leaderboard rank (0/undefined → not ranked yet). */
  rank?: number;
  totalPlayers?: number;
  /** 'white' | 'black' side to move for the current puzzle. */
  toMove?: 'white' | 'black';
  /**
   * Fallback remaining-seconds, used only when `endsAt` is unknown. The clock
   * normally computes remaining LIVE from the server `endsAt` epoch so every
   * client stays in sync (see RoyaleClockPill).
   */
  clockSeconds: number;
  /** Server-authoritative race-end epoch (ms). Drives the synced countdown. */
  endsAt?: number;
  /** Server-authoritative race-start epoch (ms) — clock holds until then. */
  startsAt?: number;
  /** True while the race is counting down (false during countdown / ended). */
  playing: boolean;
}

export default function RoyaleStrip({
  spectator = false,
  followedName,
  followedEliminated = false,
  onStopWatching,
  points = 0,
  combo = 0,
  piece = 'pawn',
  rank = 0,
  totalPlayers = 0,
  toMove = 'white',
  clockSeconds,
  endsAt,
  startsAt,
  playing,
}: Props) {
  // Always show all 5 combo tiles from the start (filled up to the current
  // combo), per the user's request — not just up to the next milestone.
  const pipCount = 5;

  if (spectator) {
    // Combined spectator bar (wireframe): [Stop watching] · username · points ·
    // combo · position · time.
    return (
      <View style={styles.root}>
        {onStopWatching && (
          <Pressable onPress={onStopWatching} style={styles.stopChip}>
            <CCText style={styles.stopChipText}>✕ Stop watching</CCText>
          </Pressable>
        )}

        <View style={styles.spectateLabel}>
          <View style={styles.spectateDot} />
          {followedName ? (
            <CCText style={styles.spectateText} numberOfLines={1}>
              {followedName}
            </CCText>
          ) : (
            <CCText style={styles.spectateText}>Spectating</CCText>
          )}
          {followedEliminated && (
            <View style={styles.outBadge}>
              <CCText style={styles.outBadgeText}>OUT</CCText>
            </View>
          )}
        </View>

        {followedName && (
          <>
            <View style={styles.sep} />
            <View style={styles.cell}>
              <CCText style={styles.points}>{points}</CCText>
              <CCText style={styles.piece}>{PIECE_GLYPHS[piece]}</CCText>
            </View>
            {combo >= 2 && (
              <View style={styles.comboBadge}>
                <CCText style={styles.comboBadgeText}>×{combo}</CCText>
              </View>
            )}
            <View style={styles.sep} />
            {/* Followed player's position */}
            <View style={styles.cell}>
              <CCText style={styles.rank}>{rank > 0 ? `#${rank}` : '—'}</CCText>
              {totalPlayers > 0 && (
                <CCText style={styles.rankTotal}>/{totalPlayers}</CCText>
              )}
            </View>
          </>
        )}

        {/* A self-contained mm:ss pill — NOT the heavy PlayChess ClockComponent
            (which sizes itself to 22% of its parent and bled out of the packed
            spectator strip, #2). This counts down locally from clockSeconds. */}
        <View style={styles.spectatorClockZone}>
          <RoyaleClockPill
            endsAt={endsAt}
            startsAt={startsAt}
            seconds={clockSeconds}
            playing={playing}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      {/* Points + piece */}
      <View style={styles.cell}>
        <CCText style={styles.points}>{points}</CCText>
        <CCText style={styles.piece}>{PIECE_GLYPHS[piece]}</CCText>
      </View>

      <View style={styles.sep} />

      {/* Combo pips. Beyond the 5 tiles, show a "×N" badge so the live combo
          stays visible once it crosses 5 (e.g. a 9-combo reads as 5 filled
          tiles + "×9"). */}
      <View style={styles.comboRow}>
        {Array.from({length: pipCount}, (_, i) => i + 1).map(n => (
          <View key={n} style={[styles.pip, n <= combo && styles.pipOn]}>
            <CCText style={[styles.pipText, n <= combo && styles.pipTextOn]}>
              {n}
            </CCText>
          </View>
        ))}
        {combo > pipCount && (
          <View style={styles.comboBadge}>
            <CCText style={styles.comboBadgeText}>×{combo}</CCText>
          </View>
        )}
      </View>

      <View style={styles.sep} />

      {/* Current leaderboard rank */}
      <View style={styles.cell}>
        <CCText style={styles.rank}>
          {rank > 0 ? `#${rank}` : '—'}
        </CCText>
        {totalPlayers > 0 && (
          <CCText style={styles.rankTotal}>/{totalPlayers}</CCText>
        )}
      </View>

      <View style={styles.sep} />

      {/* Who to play */}
      <View style={[styles.toMove, toMove === 'black' && styles.toMoveBlack]}>
        <View
          style={[styles.toMoveDot, toMove === 'black' && styles.toMoveDotBlack]}
        />
        <CCText
          style={[styles.toMoveText, toMove === 'black' && styles.toMoveTextBlack]}>
          {toMove === 'black' ? 'Black' : 'White'}
        </CCText>
      </View>

      {/* Reused PlayChess clock — the green rounded box that goes red under
          10s. Uses the same self-contained RoyaleClockPill as the spectator
          strip (the old PlayChess ClockComponent's fixed 22%-of-parent width
          crunched the mm:ss.t text in the last 10s — #2). */}
      <View style={styles.clockZone}>
        <RoyaleClockPill
            endsAt={endsAt}
            startsAt={startsAt}
            seconds={clockSeconds}
            playing={playing}
          />
      </View>
    </View>
  );
}

/**
 * RoyaleClockPill — a compact countdown pill used by BOTH the player and
 * spectator royale strips. Shows mm:ss, plus tenths (mm:ss.t) under 10s; goes
 * red under 10s. Self-sizes (unlike PlayChess ClockComponent).
 *
 * SYNCED ACROSS CLIENTS: the remaining time is computed LIVE from the
 * server-authoritative `endsAt` epoch (`endsAt - Date.now()`) on every tick —
 * NOT seeded once and decremented locally. The old seed-then-tick approach
 * drifted because each client seeded at a different wall-clock moment (start
 * broadcast + mount timing), so two clients could read several seconds apart
 * for the whole race even though endsAt was identical. Recomputing from endsAt
 * keeps everyone in lockstep (only sub-second machine clock-skew remains).
 *
 * Falls back to the static `seconds` value only when `endsAt` is unknown
 * (e.g. a pre-wire mock). `startsAt` (optional) holds the full duration during
 * the 3-2-1 countdown so the clock doesn't tick before the race begins.
 */
function RoyaleClockPill({
  endsAt,
  startsAt,
  seconds,
  playing,
}: {
  endsAt?: number;
  startsAt?: number;
  seconds: number;
  playing: boolean;
}) {
  const computeTenths = React.useCallback((): number => {
    // Before the race starts, freeze at the full duration.
    if (startsAt && Date.now() < startsAt) {
      return Math.max(0, Math.round(seconds * 10));
    }
    if (endsAt) {
      return Math.max(0, Math.round((endsAt - Date.now()) / 100));
    }
    return Math.max(0, Math.round(seconds * 10));
  }, [endsAt, startsAt, seconds]);

  const [tenths, setTenths] = useState(computeTenths);

  // Re-sync immediately when the authoritative timestamps change.
  useEffect(() => {
    setTenths(computeTenths());
  }, [computeTenths]);

  useEffect(() => {
    if (!playing) {
      return;
    }
    const id = setInterval(() => {
      // Recompute from the authoritative epoch (not prev-1) so we never drift.
      setTenths(computeTenths());
    }, 100);
    return () => clearInterval(id);
  }, [playing, computeTenths]);

  const totalSec = Math.floor(tenths / 10);
  const mm = Math.floor(totalSec / 60);
  const ss = String(totalSec % 60).padStart(2, '0');
  const urgent = totalSec < 10;
  return (
    <View style={[styles.specClockPill, urgent && styles.specClockPillUrgent]}>
      <CCText style={[styles.specClockText, urgent && styles.specClockTextUrgent]}>
        {mm}:{ss}
        {urgent && (
          <CCText style={styles.specClockTenths}>.{tenths % 10}</CCText>
        )}
      </CCText>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: CREAM3,
    paddingLeft: 18,
    paddingRight: 12,
    // Fixed height so the reused ClockComponent's height:'100%' resolves.
    height: 60,
  },
  cell: {flexDirection: 'row', alignItems: 'baseline', gap: 5},
  sep: {width: 1, height: 28, backgroundColor: CREAM3},
  points: {fontSize: 28, fontWeight: '800', color: BROWN, lineHeight: 30},
  piece: {fontSize: 18, color: TEAL_DARK, lineHeight: 20},
  comboRow: {flexDirection: 'row', gap: 5},
  pip: {
    width: 24,
    height: 24,
    borderRadius: 6,
    backgroundColor: CREAM3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pipOn: {backgroundColor: YELLOW},
  pipText: {fontSize: 11, fontWeight: '800', color: 'rgba(77,63,55,0.35)'},
  pipTextOn: {color: BROWN},
  comboBadge: {
    marginLeft: 4,
    paddingHorizontal: 8,
    height: 24,
    borderRadius: 6,
    backgroundColor: YELLOW,
    alignItems: 'center',
    justifyContent: 'center',
  },
  comboBadgeText: {fontSize: 12, fontWeight: '800', color: BROWN},
  rank: {fontSize: 24, fontWeight: '800', color: BROWN, lineHeight: 26},
  rankTotal: {fontSize: 13, fontWeight: '700', color: 'rgba(77,63,55,0.4)'},
  toMove: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#EAF6F3',
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  toMoveBlack: {backgroundColor: '#EEE9DD'},
  toMoveDot: {width: 9, height: 9, borderRadius: 999, backgroundColor: '#fff', borderWidth: 1.5, borderColor: BROWN},
  toMoveDotBlack: {backgroundColor: BROWN},
  toMoveText: {fontSize: FontSizes[13] || 13, fontWeight: '700', color: TEAL_DARK},
  toMoveTextBlack: {color: BROWN},
  // ClockComponent hard-codes width:'22%' height:'100%' of its parent (built
  // for PlayChess's ~500px player bar). We give it a FLEXED zone that absorbs
  // the strip's spare width, with a minWidth so 22% always clears the "MM:SS"
  // digits (≈ 70px → zone ≥ 320). Right-aligned, with the strip's right
  // padding giving clearance so the pill is fully visible, never clipped.
  clockZone: {
    flex: 1,
    height: 46,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  spectateLabel: {flexDirection: 'row', alignItems: 'center', gap: 8},
  spectateDot: {
    width: 9,
    height: 9,
    borderRadius: 999,
    backgroundColor: '#CD2C39',
  },
  spectateText: {
    fontSize: FontSizes[15] || 15,
    fontWeight: '800',
    color: BROWN,
    letterSpacing: 0.3,
  },
  outBadge: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 5,
    backgroundColor: 'rgba(205,44,57,0.12)',
  },
  outBadgeText: {fontSize: 9, fontWeight: '800', color: '#CD2C39'},
  stopChip: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: 'rgba(205,44,57,0.10)',
    borderWidth: 1,
    borderColor: 'rgba(205,44,57,0.30)',
  },
  stopChipText: {fontSize: FontSizes[13] || 13, fontWeight: '800', color: '#CD2C39'},
  // Compact, right-aligned zone for the spectator's own mm:ss pill — fixed so
  // it never overflows the packed spectator strip (#2).
  spectatorClockZone: {
    marginLeft: 'auto',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  specClockPill: {
    backgroundColor: '#2A9D8F',
    borderRadius: 8,
    paddingVertical: 7,
    // FIXED width (not minWidth) so the pill doesn't shrink/grow when the
    // tenths digit appears/disappears in the last 10s (#6). Sized to fit
    // "00:00.0" with tabular-nums.
    width: 96,
    alignItems: 'center',
    justifyContent: 'center',
  },
  specClockPillUrgent: {backgroundColor: '#CD2C39'},
  specClockText: {
    fontSize: FontSizes[18] || 18,
    fontWeight: '800',
    color: '#fff',
    fontVariant: ['tabular-nums'],
  },
  specClockTextUrgent: {color: '#fff'},
  specClockTenths: {
    fontSize: FontSizes[14] || 14,
    fontWeight: '800',
    color: '#fff',
    fontVariant: ['tabular-nums'],
  },
});
